
import { BIOBANK_ROUTES } from "../constants/routes";

const sel = (id) => `[data-tour-id="${id}"]`;

export const BIOBANK_TOUR_STEPS = [
  {
    route: BIOBANK_ROUTES.DASHBOARD,
    selector: sel("nav-Dashboard"),
    side: "right",
    align: "start",
    popover: {
      title: "Welcome to Biobank",
      description:
        "This tour walks through all ten Biobank areas, in the order you'll normally use them — starting with the Dashboard and ending at the Email Center. Close it anytime and restart it later from this Dashboard.",
    },
  },
  {
    route: BIOBANK_ROUTES.DASHBOARD,
    selector: sel("dashboard-sample-types"),
    side: "bottom",
    align: "start",
    popover: {
      title: "Sample Type Management",
      description:
        "Sample types are the building blocks used everywhere downstream — every kit and every collected sample references one of these (e.g. a specific tube or aliquot type, shown by its letter code).",
    },
  },
  {
    route: BIOBANK_ROUTES.DASHBOARD,
    selector: sel("tour-add-sample-type"),
    side: "left",
    align: "start",
    popover: {
      title: "Add Sample Type",
      description:
        "Click here to define a new sample type — its letter code, short/long name, volume, quantity, and container. Once created, it becomes selectable in Kit Type Management.",
    },
  },
  {
    route: BIOBANK_ROUTES.DASHBOARD,
    selector: sel("tour-edit-sample-type"),
    side: "left",
    align: "start",
    popover: {
      title: "Edit a sample type",
      description:
        "Click the pencil icon on any row to update that sample type's details.",
    },
  },
  {
    route: BIOBANK_ROUTES.DASHBOARD,
    selector: sel("dashboard-kit-types"),
    side: "bottom",
    align: "start",
    popover: {
      title: "Kit Type Management",
      description:
        "A kit type bundles together the sample types a specific study expects in one physical kit. You'll pick one of these whenever a new kit gets registered in Collection Kits.",
    },
  },
  {
    route: BIOBANK_ROUTES.DASHBOARD,
    selector: sel("tour-add-kit-type"),
    side: "left",
    align: "start",
    popover: {
      title: "Add Kit Type",
      description:
        "Click here to create a new kit type — give it a code, choose the sample types it should contain, and select which studies use it.",
    },
  },
  {
    route: BIOBANK_ROUTES.DASHBOARD,
    selector: sel("tour-edit-kit-type"),
    side: "left",
    align: "start",
    popover: {
      title: "Edit a kit type",
      description:
        "Click the pencil icon on any row to change its expected sample types, studies, or comments.",
    },
  },


  {
    route: BIOBANK_ROUTES.DASHBOARD,
    selector: sel("nav-Collection Kits"),
    side: "right",
    align: "start",
    popover: {
      title: "Next: Collection Kits",
      description:
        "A physical collection kit has to be registered here before it can be used on a participant. Let's take a look.",
    },
  },
  {
    route: BIOBANK_ROUTES.COLLECTION_KITS,
    selector: sel("tour-register-kit"),
    side: "bottom",
    align: "end",
    popover: {
      title: "Register a new kit",
      description:
        "Click here whenever a new physical kit arrives. You'll pick its Kit Type and type the 5-digit number printed on its label — together they form the Batch ID everything downstream refers to.",
    },
  },
  {
    route: BIOBANK_ROUTES.COLLECTION_KITS,
    selector: sel("tour-kit-type-filter"),
    side: "bottom",
    align: "end",
    popover: {
      title: "Filter by kit type",
      description:
        "Narrow the list down to one kit type — handy when a study has a lot of registered kits and you only want to see its own.",
    },
  },
  {
    route: BIOBANK_ROUTES.COLLECTION_KITS,
    selector: sel("tour-kit-table"),
    side: "top",
    align: "start",
    popover: {
      title: "Kit number & status",
      description:
        "Every registered kit shows here with its Kit Number, Batch Status (Available → Reserved/Partial → Used), how many samples have been collected into it, and which sample types still need collecting.",
    },
  },

 
  {
    route: BIOBANK_ROUTES.COLLECTION_KITS,
    selector: sel("nav-Participant Scheduling"),
    side: "right",
    align: "start",
    popover: {
      title: "Next: Participant Scheduling",
      description:
        "This is where a participant gets onto the calendar for a visit.",
    },
  },
  {
    route: BIOBANK_ROUTES.PARTICIPANT_SCHEDULING,
    selector: sel("tour-schedule-participant"),
    side: "bottom",
    align: "end",
    popover: {
      title: "Schedule a participant",
      description:
        "Click here to schedule a participant. You'll first check whether they already have an account by MRN — if not, you can register them on the spot — then pick a date, time, ordering physician, and the studies for the visit.",
    },
  },
  {
    route: BIOBANK_ROUTES.PARTICIPANT_SCHEDULING,
    selector: sel("tour-schedule-date-filter"),
    side: "bottom",
    align: "start",
    popover: {
      title: "Filter by appointment date",
      description:
        "Search narrows the schedule to appointments within a date range; Reset clears it back to the default window (today through next month).",
    },
  },
  {
    route: BIOBANK_ROUTES.PARTICIPANT_SCHEDULING,
    selector: sel("tour-schedule-table"),
    side: "top",
    align: "start",
    popover: {
      title: "Scheduled participants",
      description:
        "Everyone scheduled for the selected date range — their appointment time, scheduled studies, and consent status. Use Reschedule on any row to change its date/time.",
    },
  },

  {
    route: BIOBANK_ROUTES.PARTICIPANT_SCHEDULING,
    selector: sel("nav-Participant Check-In"),
    side: "right",
    align: "start",
    popover: {
      title: "Next: Participant Check-In",
      description:
        "Once a participant arrives for their visit, this is where staff check them in.",
    },
  },
  {
    route: BIOBANK_ROUTES.PARTICIPANT_CHECKIN,
    selector: sel("tour-checkin-date-filter"),
    side: "bottom",
    align: "start",
    popover: {
      title: "Filter by appointment date",
      description:
        "Same date-range search as Scheduling — narrow today's check-in list down to a specific range.",
    },
  },
  {
    route: BIOBANK_ROUTES.PARTICIPANT_CHECKIN,
    selector: sel("tour-checkin-worklist"),
    side: "top",
    align: "start",
    popover: {
      title: "Today's check-in worklist",
      description:
        "Every scheduled participant shows up here. \"Check In\" only becomes available once their consent status shows Complete — click it to confirm their info, collect any missing consent forms, and record them as checked in.",
    },
  },
  {
    route: BIOBANK_ROUTES.PARTICIPANT_CHECKIN,
    selector: sel("tour-dropin-participant"),
    side: "bottom",
    align: "end",
    popover: {
      title: "Drop-In Participant",
      description:
        "Click here for a participant who showed up without an appointment. It reuses the same account-verification flow as scheduling, just booked for right now instead of a future date.",
    },
  },

  {
    route: BIOBANK_ROUTES.PARTICIPANT_CHECKIN,
    selector: sel("nav-Sample Collection"),
    side: "right",
    align: "start",
    popover: {
      title: "Next: Sample Collection",
      description:
        "Once a participant is checked in, their samples are captured here.",
    },
  },
  {
    route: BIOBANK_ROUTES.SAMPLE_COLLECTION,
    selector: sel("tour-collection-tab-all"),
    clickBefore: sel("tour-collection-tab-all"),
    side: "bottom",
    align: "start",
    popover: {
      title: "All Collections",
      description:
        "The default view — every checked-in participant ready for a first-time sample collection.",
    },
  },
  {
    route: BIOBANK_ROUTES.SAMPLE_COLLECTION,
    selector: sel("tour-collection-date-filter"),
    clickBefore: sel("tour-collection-tab-all"),
    side: "bottom",
    align: "start",
    popover: {
      title: "Filter by date",
      description: "Narrow this list down to a specific appointment date range.",
    },
  },
  {
    route: BIOBANK_ROUTES.SAMPLE_COLLECTION,
    selector: sel("tour-collection-table"),
    clickBefore: sel("tour-collection-tab-all"),
    side: "top",
    align: "start",
    popover: {
      title: "Collect samples",
      description:
        "A \"NOT CHECKED IN\" chip means collection is blocked until Check-In is done. Otherwise, click Collect to apply a kit and record what was actually collected.",
    },
  },
  {
    route: BIOBANK_ROUTES.SAMPLE_COLLECTION,
    selector: sel("tour-collection-tab-followup"),
    side: "bottom",
    align: "start",
    popover: {
      title: "Follow-Up tab",
      description:
        "Click here to switch to milestone follow-up visits — a faster, single-step form instead of the full first-time collection wizard.",
    },
  },
  {
    route: BIOBANK_ROUTES.SAMPLE_COLLECTION,
    selector: sel("tour-collection-table"),
    clickBefore: sel("tour-collection-tab-followup"),
    side: "top",
    align: "start",
    popover: {
      title: "Follow-Up collections",
      description:
        "Participants due for a milestone (H1–H10 style) visit. Click Collect here to jump straight to the quick follow-up sample form — no batch or authorization step needed.",
    },
  },


  {
    route: BIOBANK_ROUTES.SAMPLE_COLLECTION,
    selector: sel("nav-Sample Processing"),
    side: "right",
    align: "start",
    popover: {
      title: "Next: Sample Processing",
      description: "Collected samples get split into aliquots here.",
    },
  },
  {
    route: BIOBANK_ROUTES.SAMPLE_PROCESSING,
    selector: sel("tour-processing-filters"),
    side: "right",
    align: "start",
    popover: {
      title: "Filter by type & quality",
      description:
        "Check any Sample Type or Sample Quality boxes to narrow the list down — useful when the unprocessed queue is long. Clear resets both filters.",
    },
  },
  {
    route: BIOBANK_ROUTES.SAMPLE_PROCESSING,
    selector: sel("tour-processing-table"),
    side: "left",
    align: "start",
    popover: {
      title: "Process a sample",
      description:
        "This list shows samples collected but not yet processed. Click Process Sample on a row to split it into aliquots, generate their barcodes for printing, and assign temporary storage before shipment.",
    },
  },

  {
    route: BIOBANK_ROUTES.SAMPLE_PROCESSING,
    selector: sel("nav-Aliquot Management"),
    side: "right",
    align: "start",
    popover: {
      title: "Next: Aliquot Management",
      description:
        "This is freezer inventory bookkeeping for aliquots — separate from creating them in Sample Processing. It has four tabs, one per stage of an aliquot's life.",
    },
  },
  {
    route: BIOBANK_ROUTES.ALIQUOT_MANAGEMENT,
    selector: sel("tour-aliquot-tab-checkin"),
    clickBefore: sel("tour-aliquot-tab-checkin"),
    side: "bottom",
    align: "start",
    popover: {
      title: "Tab 1 — Check-In",
      description:
        "Assign a permanent freezer/rack/box/position to a newly-processed aliquot before it's ready for use.",
    },
  },
  {
    route: BIOBANK_ROUTES.ALIQUOT_MANAGEMENT,
    selector: sel("tour-aliquot-checkin-search"),
    clickBefore: sel("tour-aliquot-tab-checkin"),
    side: "bottom",
    align: "start",
    popover: {
      title: "Search by barcode",
      description:
        "Type a barcode (kit/batch ID prefix works too) to jump straight to the aliquots waiting on that kit, instead of scrolling the full not-checked-in list.",
    },
  },
  {
    route: BIOBANK_ROUTES.ALIQUOT_MANAGEMENT,
    selector: sel("tour-aliquot-tab-checkout"),
    clickBefore: sel("tour-aliquot-tab-checkout"),
    side: "bottom",
    align: "start",
    popover: {
      title: "Tab 2 — Check-Out",
      description:
        "The opposite direction: select checked-in aliquots to ship out to a Project (their destination record).",
    },
  },
  {
    route: BIOBANK_ROUTES.ALIQUOT_MANAGEMENT,
    selector: sel("tour-aliquot-checkout-filter"),
    clickBefore: sel("tour-aliquot-tab-checkout"),
    side: "bottom",
    align: "start",
    popover: {
      title: "Filter & select aliquots",
      description:
        "Narrow the list by barcode, then check the rows you want and use Check-Out Selected — you'll be asked to confirm the count and pick a destination Project before shipping.",
    },
  },
  {
    route: BIOBANK_ROUTES.ALIQUOT_MANAGEMENT,
    selector: sel("tour-aliquot-tab-history"),
    clickBefore: sel("tour-aliquot-tab-history"),
    side: "bottom",
    align: "start",
    popover: {
      title: "Tab 3 — Check-out history",
      description: "A read-only log of every past checkout — what shipped, when, and to which Project.",
    },
  },
  {
    route: BIOBANK_ROUTES.ALIQUOT_MANAGEMENT,
    selector: sel("tour-aliquot-tab-usage"),
    clickBefore: sel("tour-aliquot-tab-usage"),
    side: "bottom",
    align: "start",
    popover: {
      title: "Tab 4 — Usage history",
      description: "Another read-only log, tracking aliquot usage over time.",
    },
  },


  {
    route: BIOBANK_ROUTES.ALIQUOT_MANAGEMENT,
    selector: sel("nav-Follow-Up Management"),
    side: "right",
    align: "start",
    popover: {
      title: "Next: Follow-Up Management",
      description:
        "Participants come due for milestone follow-up visits — this is where staff track and act on them, across three tabs.",
    },
  },
  {
    route: BIOBANK_ROUTES.FOLLOWUP_MANAGEMENT,
    selector: sel("tour-followup-tab-actions"),
    clickBefore: sel("tour-followup-tab-actions"),
    side: "bottom",
    align: "start",
    popover: {
      title: "Tab 1 — Action Items",
      description:
        "Your daily worklist — every follow-up currently due or overdue. This is the tab you'll live in day-to-day.",
    },
  },
  {
    route: BIOBANK_ROUTES.FOLLOWUP_MANAGEMENT,
    selector: sel("tour-followup-actions-table"),
    clickBefore: sel("tour-followup-tab-actions"),
    side: "top",
    align: "start",
    popover: {
      title: "Next Action button",
      description:
        "Each row's Next Action button always tells you the correct next step — sending an email, mailing a kit, recording a ship-back, or opening a new collection. Mark Missed appears once a row is over 30 days overdue.",
    },
  },
  {
    route: BIOBANK_ROUTES.FOLLOWUP_MANAGEMENT,
    selector: sel("tour-followup-tab-future"),
    clickBefore: sel("tour-followup-tab-future"),
    side: "bottom",
    align: "start",
    popover: {
      title: "Tab 2 — Future",
      description: "A read-only look ahead at follow-ups that aren't due yet.",
    },
  },
  {
    route: BIOBANK_ROUTES.FOLLOWUP_MANAGEMENT,
    selector: sel("tour-followup-tab-complete"),
    clickBefore: sel("tour-followup-tab-complete"),
    side: "bottom",
    align: "start",
    popover: {
      title: "Tab 3 — Complete Dashboard",
      description: "A read-only review of follow-ups that have already been completed.",
    },
  },

  {
    route: BIOBANK_ROUTES.FOLLOWUP_MANAGEMENT,
    selector: sel("nav-Project Management"),
    side: "right",
    align: "start",
    popover: {
      title: "Next: Project Management",
      description:
        "A Project is an administrative record — separate from the Studies participants consent to.",
    },
  },
  {
    route: BIOBANK_ROUTES.PROJECT_MANAGEMENT,
    selector: sel("tour-create-project"),
    side: "bottom",
    align: "end",
    popover: {
      title: "Create a project",
      description:
        "Click here to register a new project — its PI, institution, funding source, and IRB number. Projects mainly matter later as the shipment destination when checking aliquots out in Aliquot Management.",
    },
  },
  {
    route: BIOBANK_ROUTES.PROJECT_MANAGEMENT,
    selector: sel("tour-project-search"),
    side: "bottom",
    align: "start",
    popover: {
      title: "Filter projects",
      description:
        "Narrow the list by IRB number (starts-with) or project name (contains), then Search — or Clear to reset both.",
    },
  },
  {
    route: BIOBANK_ROUTES.PROJECT_MANAGEMENT,
    selector: sel("tour-project-table"),
    side: "top",
    align: "start",
    popover: {
      title: "Project list",
      description:
        "Click any IRB Number to open that project's full details, where you can edit every field inline.",
    },
  },


  {
    route: BIOBANK_ROUTES.PROJECT_MANAGEMENT,
    selector: sel("nav-Email Center"),
    side: "right",
    align: "start",
    popover: {
      title: "Next: Email Center",
      description: "Last stop — where follow-up emails are tracked and edited.",
    },
  },
  {
    route: BIOBANK_ROUTES.EMAIL_CENTER,
    selector: sel("tour-email-tab-messages"),
    clickBefore: sel("tour-email-tab-messages"),
    side: "bottom",
    align: "start",
    popover: {
      title: "Messages",
      description:
        "A log of automated follow-up emails, not a compose tool — most emails here are triggered automatically from Follow-Up Management's Action Items.",
    },
  },
  {
    route: BIOBANK_ROUTES.EMAIL_CENTER,
    selector: sel("tour-email-messages-table"),
    clickBefore: sel("tour-email-tab-messages"),
    side: "top",
    align: "start",
    popover: {
      title: "Reading the log",
      description:
        "OUT means sent by the system, IN means a reply came in; a red FAILED status means delivery didn't go through. Use the match icon to link a reply to the right participant, and filter by \"Unmatched\" to find ones that still need it.",
    },
  },
  {
    route: BIOBANK_ROUTES.EMAIL_CENTER,
    selector: sel("tour-email-tab-templates"),
    clickBefore: sel("tour-email-tab-templates"),
    side: "bottom",
    align: "start",
    popover: {
      title: "Templates",
      description:
        "Click here to see and edit the wording of the automated follow-up emails.",
    },
  },
  {
    route: BIOBANK_ROUTES.EMAIL_CENTER,
    selector: sel("tour-email-templates-table"),
    clickBefore: sel("tour-email-tab-templates"),
    side: "top",
    align: "start",
    popover: {
      title: "Editing a template",
      description:
        "Click Edit on any template to rewrite its Subject/Body. Variable chips insert placeholders like {{patientFirstName}} at your cursor — required ones must appear somewhere before you can save. Preview shows the real rendered result first. That's the full tour — welcome aboard!",
    },
  },
];

export default BIOBANK_TOUR_STEPS;
